import { LoginResponse } from '@/types/auth';

const TOKEN_KEY = 'pickem_auth_token';
const REFRESH_TOKEN_KEY = 'pickem_refresh_token';
const USER_KEY = 'pickem_user';
const SSO_TOKEN_KEY = 'pickem_sso_token';

/**
 * Extract token from URL query string
 * In dev mode, returns '12345' if no token is found
 */
export const extractTokenFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  if (token) {
    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;

    // Only clean up URL if not in iframe (to allow multiple components to read the token)
    if (!isInIframe) {
      // Clean up URL by removing the token parameter
      params.delete('token');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    return token;
  }

  // If COERCE_USER_TOKEN is enabled, return hardcoded token for testing
  if (import.meta.env.VITE_COERCE_USER_TOKEN === '1') {
    return '12345';
  }

  return null;
};

/**
 * Validate JWT token format (basic validation)
 */
export const isValidTokenFormat = (token: string): boolean => {
  if (!token) return false;
  const parts = token.split('.');
  return parts.length === 3;
};

/**
 * Decode JWT token payload (without verification)
 */
export const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  return Date.now() >= expirationTime;
};

/**
 * Store authentication data
 */
export const storeAuthData = (data: LoginResponse): void => {
  if (data.token) {
    sessionStorage.setItem(SSO_TOKEN_KEY, data.token);
  }
  if (data.refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  }
  if (data.user) {
    sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
  }
};

/**
 * Get stored token
 */
export const getStoredToken = (): string | null => {
  return sessionStorage.getItem(TOKEN_KEY);
};

/**
 * Get stored refresh token
 */
export const getStoredRefreshToken = (): string | null => {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Get stored user
 */
export const getStoredUser = (): any | null => {
  const userStr = sessionStorage.getItem(USER_KEY);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Set stored user
 */
export const setStoredUser = (user: any): void => {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
};

/**
 * Get stored SSO token from sessionStorage
 * This retrieves the JWT token returned from /api/login after play-box authentication
 */
export const getStoredSsoToken = (): string | null => {
  return sessionStorage.getItem(SSO_TOKEN_KEY);
};

/**
 * Clear all authentication data including SSO tokens
 * @deprecated This function is now a no-op as tokens are read from URL only
 */
export const clearAuthData = (): void => {
  // No-op: Tokens are now read directly from URL parameters
  // Keeping this function for backwards compatibility
};

/**
 * Auto-login check on app mount
 */
export const checkAutoLogin = (): { token: string | null; needsRefresh: boolean } => {
  // First check URL for token (also handles dev mode fallback)
  const urlToken = extractTokenFromUrl();
  if (urlToken) {
    // In dev mode, the token might be '12345' which is not a valid JWT format
    // but we should still use it for authentication
    if (import.meta.env.DEV || isValidTokenFormat(urlToken)) {
      return { token: urlToken, needsRefresh: false };
    }
  }

  // Then check stored token
  const storedToken = getStoredToken();
  if (storedToken && isValidTokenFormat(storedToken)) {
    const expired = isTokenExpired(storedToken);
    return { token: expired ? null : storedToken, needsRefresh: expired };
  }

  return { token: null, needsRefresh: false };
};
